import { c as _c } from "react/compiler-runtime";
import { Input } from '@xefi/x-react/form';
import { Tooltip } from '@xefi/x-react/tooltip';
import { mergeTailwindClasses } from '@xefi/x-react/utils';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorHelper } from '@/app/(survey-taking)/_components/ErrorHelper';
import { isQuestionOverflowed } from '@/app/(survey-taking)/_components/SurveyTakingPage/SurveyTakingPage';
import { CheckBoxField } from '@/app/(survey-taking)/_components/SurveyTakingQuestion/CheckBoxField';
import { ParticipantBlock } from '@/app/(survey-taking)/_components/SurveyTakingQuestion/ParticipantBlock';
import { RadioButtonsField } from '@/app/(survey-taking)/_components/SurveyTakingQuestion/RadioButtonsField';
import { SelectField } from '@/app/(survey-taking)/_components/SurveyTakingQuestion/SelectField';
import { FileUploader } from '@/components/FileUploader';
import { InputWithMaxLength } from '@/components/InputWithMaxLength';
import { DateField } from '@/components/SurveyFields/DateField';
import { DateHourField } from '@/components/SurveyFields/DateHourField';
import { HourField } from '@/components/SurveyFields/HourField';
import { PhoneField } from '@/components/SurveyFields/PhoneField';
import { RatingStarField } from '@/components/SurveyFields/RatingStarField';
import { QuestionHeader } from '@/components/SurveyQuestion/QuestionHeader';
import { QuestionHelper } from '@/components/SurveyQuestion/QuestionHelper';
import { TextareaWithMaxLength } from '@/components/TextareaWithMaxLength';
import { availableExt } from '@/config/config';
import type { Session } from '@/models/event';
import type { FileType } from '@/models/inputFields';
import type { Question } from '@/models/question';
import type { SurveyTaking } from '@/models/survey';
import { makeLinkedQuestionLabel } from '@/utils/questions';
type SurveyTakingQuestionProps = {
  question: Partial<Question>;
  indexLabel: string;
  questionPath: string;
  disabled?: boolean;
  errorMap?: Map<string, string>;
  handleChange: (path: string, value: unknown, oldValue?: string) => void;
  sessions: Session[];
  currentSurvey?: SurveyTaking;
};
export const SurveyTakingQuestion = t0 => {
  const $ = _c(192);
  const {
    question,
    indexLabel,
    questionPath,
    handleChange,
    disabled: t1,
    errorMap,
    sessions,
    currentSurvey
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const [tooltipLabel, setTooltipLabel] = useState("");
  const media = Array.isArray(question.media) && question.media.length > 0 ? question.media[0] : null;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const canvas = document.createElement("canvas");
    t3 = canvas.getContext("2d");
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const context = t3;
  if (!context) {
    throw new Error("Canvas 2D context is not supported.");
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (text, font) => {
      context.font = font;
      return context.measureText(text).width;
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  t2 = t4;
  const getTextWidth = t2;
  let t5;
  if ($[2] !== errorMap || $[3] !== handleChange || $[4] !== indexLabel || $[5] !== question.linked_choices || $[6] !== question.select_value || $[7] !== questionPath || $[8] !== sessions) {
    t5 = () => {
      const choiceIndex = question.linked_choices?.findIndex(choice => choice.id === question.select_value);
      if (choiceIndex === -1 || choiceIndex === undefined) {
        return null;
      }
      const choice_0 = question.linked_choices?.[choiceIndex];
      const condCount = choice_0?.linked_questions?.length ?? 0;
      return choice_0?.linked_questions?.flatMap((cond, condIndex) => !cond.is_limit_overflowed && cond.is_visible ? <SurveyTakingQuestion key={cond.id} question={cond} indexLabel={makeLinkedQuestionLabel(indexLabel, choiceIndex, condIndex, condCount)} questionPath={`${questionPath}.linked_choices[${choiceIndex}].linked_questions[${condIndex}]`} handleChange={(path, value, oldValue) => handleChange(`linked_choices[${choiceIndex}].linked_questions[${condIndex}].${path}`, value, oldValue)} sessions={sessions} errorMap={errorMap} disabled={isQuestionOverflowed(cond)} /> : null);
    };
    $[2] = errorMap;
    $[3] = handleChange;
    $[4] = indexLabel;
    $[5] = question.linked_choices;
    $[6] = question.select_value;
    $[7] = questionPath;
    $[8] = sessions;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const renderSelectLinked = t5;
  let t6;
  if ($[10] !== question.linked_choices) {
    t6 = idChoice => {
      const selectedChoice = question.linked_choices?.find(element => element.id === idChoice);
      setTooltipLabel(selectedChoice && getTextWidth(selectedChoice.label, "roboto") > 300 ? selectedChoice.label : "");
    };
    $[10] = question.linked_choices;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const tooltipLabelSetter = t6;
  let t7;
  let t8;
  if ($[12] !== question.select_value || $[13] !== tooltipLabelSetter) {
    t7 = () => {
      tooltipLabelSetter(question.select_value);
    };
    t8 = [question.select_value, tooltipLabelSetter];
    $[12] = question.select_value;
    $[13] = tooltipLabelSetter;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  let t9;
  if ($[16] !== question.answer) {
    t9 = question.answer && question.answer.file instanceof File ? [question.answer.file] : [];
    $[16] = question.answer;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const initialFiles = t9;
  const [files, setFiles] = useState(initialFiles);
  let t10;
  let t11;
  if ($[18] !== question.answer) {
    t10 = () => {
      if (question.answer && question.answer.file instanceof File) {
        setFiles([question.answer.file]);
      } else {
        if (!question.answer) {
          setFiles([]);
        }
      }
    };
    t11 = [question.answer];
    $[18] = question.answer;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  if ($[21] !== handleChange) {
    t12 = newFiles => {
      setFiles(newFiles);
      if (newFiles.length > 0) {
        const f = newFiles[0];
        handleChange("answer", {
          file: f,
          size: f.size,
          name: f.name,
          type: f.type
        } as FileType);
      } else {
        handleChange("answer", undefined);
      }
    };
    $[21] = handleChange;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const setFilesAndPropagate = t12;
  let t13;
  if ($[23] !== files || $[24] !== setFilesAndPropagate) {
    t13 = fileToRemove => {
      const remaining = files.filter(f_0 => f_0 !== fileToRemove);
      setFilesAndPropagate(remaining);
    };
    $[23] = files;
    $[24] = setFilesAndPropagate;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleRemove = t13;
  const t14 = disabled && "pointer-events-none";
  let t15;
  if ($[26] !== t14) {
    t15 = mergeTailwindClasses("relative z-10 text-sm", t14);
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== indexLabel || $[29] !== t) {
    t16 = t("question.label", {
      number: indexLabel
    });
    $[28] = indexLabel;
    $[29] = t;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16) {
    t17 = <div className="bg-content1-100 w-fit rounded-t px-2 py-1 font-semibold">{t16}</div>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const t18 = question?.is_required;
  const t19 = question.question_type?.slug;
  const t20 = question?.label;
  let t21;
  if ($[33] !== t20) {
    t21 = <p>{t20}</p>;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== currentSurvey?.type.slug || $[36] !== question.right_grade || $[37] !== t) {
    t22 = currentSurvey?.type.slug === "graded" && <p>{question?.right_grade}{" "}{t("question.pointScale.pointsAbbreviation", {
        count: Number(question?.right_grade)
      })}</p>;
    $[35] = currentSurvey?.type.slug;
    $[36] = question.right_grade;
    $[37] = t;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t21 || $[40] !== t22) {
    t23 = <div className="flex w-full justify-between">{t21}{t22}</div>;
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== disabled || $[43] !== media || $[44] !== t18 || $[45] !== t19 || $[46] !== t23) {
    t24 = <QuestionHeader isDisabled={disabled} isRequired={t18} questionType={t19} media={media}>{t23}</QuestionHeader>;
    $[42] = disabled;
    $[43] = media;
    $[44] = t18;
    $[45] = t19;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="bg-border h-px" />;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== errorMap || $[50] !== question.id) {
    t26 = errorMap?.has(question.id) && <ErrorHelper error={errorMap.get(question.id)} questionId={question.id} />;
    $[49] = errorMap;
    $[50] = question.id;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  const t27 = question?.hint;
  let t28;
  if ($[52] !== t27) {
    t28 = <QuestionHelper type="hint">{t27}</QuestionHelper>;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  bb0: {
    switch (question.question_type?.slug ?? "email") {
      case "email":
        {
          const t30 = question?.answer;
          let t31;
          if ($[54] !== handleChange) {
            t31 = event_10 => handleChange(`answer`, event_10.target.value);
            $[54] = handleChange;
            $[55] = t31;
          } else {
            t31 = $[55];
          }
          let t32;
          if ($[56] !== t) {
            t32 = t("participant.email").toString();
            $[56] = t;
            $[57] = t32;
          } else {
            t32 = $[57];
          }
          let t33;
          if ($[58] !== disabled || $[59] !== t30 || $[60] !== t31 || $[61] !== t32) {
            t33 = <Input radius="md" isDisabled={disabled} type="email" value={t30} onChange={t31} placeholder={t32} />;
            $[58] = disabled;
            $[59] = t30;
            $[60] = t31;
            $[61] = t32;
            $[62] = t33;
          } else {
            t33 = $[62];
          }
          t29 = t33;
          break bb0;
        }
      case "text":
        {
          const t30 = question?.answer;
          let t31;
          if ($[63] !== handleChange) {
            t31 = event_9 => handleChange(`answer`, event_9.target.value);
            $[63] = handleChange;
            $[64] = t31;
          } else {
            t31 = $[64];
          }
          let t32;
          if ($[65] !== disabled || $[66] !== t30 || $[67] !== t31) {
            t32 = <InputWithMaxLength radius="md" isDisabled={disabled} maxLength={140} type="text" value={t30} onChange={t31} />;
            $[65] = disabled;
            $[66] = t30;
            $[67] = t31;
            $[68] = t32;
          } else {
            t32 = $[68];
          }
          t29 = t32;
          break bb0;
        }
      case "textarea":
        {
          const t30 = question?.answer;
          let t31;
          if ($[69] !== handleChange) {
            t31 = event_8 => handleChange(`answer`, event_8.target.value);
            $[69] = handleChange;
            $[70] = t31;
          } else {
            t31 = $[70];
          }
          let t32;
          if ($[71] !== t) {
            t32 = t("comment.label").toString();
            $[71] = t;
            $[72] = t32;
          } else {
            t32 = $[72];
          }
          let t33;
          if ($[73] !== disabled || $[74] !== t30 || $[75] !== t31 || $[76] !== t32) {
            t33 = <TextareaWithMaxLength value={t30} onChange={t31} maxLength={1500} isDisabled={disabled} placeholder={t32} />;
            $[73] = disabled;
            $[74] = t30;
            $[75] = t31;
            $[76] = t32;
            $[77] = t33;
          } else {
            t33 = $[77];
          }
          t29 = t33;
          break bb0;
        }
      case "number":
        {
          if (question?.personalization?.option === "phone") {
            const t30 = question?.answer;
            let t31;
            if ($[78] !== handleChange) {
              t31 = value_2 => handleChange(`answer`, value_2);
              $[78] = handleChange;
              $[79] = t31;
            } else {
              t31 = $[79];
            }
            let t32;
            if ($[80] !== disabled || $[81] !== t30 || $[82] !== t31) {
              t32 = <PhoneField value={t30} disabled={disabled} onChange={t31} />;
              $[80] = disabled;
              $[81] = t30;
              $[82] = t31;
              $[83] = t32;
            } else {
              t32 = $[83];
            }
            t29 = t32;
            break bb0;
          } else {
            const t30 = question?.answer;
            let t31;
            if ($[84] !== handleChange) {
              t31 = event_7 => handleChange(`answer`, event_7.target.value);
              $[84] = handleChange;
              $[85] = t31;
            } else {
              t31 = $[85];
            }
            let t32;
            if ($[86] !== t) {
              t32 = t("numeric.example").toString();
              $[86] = t;
              $[87] = t32;
            } else {
              t32 = $[87];
            }
            let t33;
            if ($[88] !== disabled || $[89] !== t30 || $[90] !== t31 || $[91] !== t32) {
              t33 = <Input type="number" radius="md" isDisabled={disabled} value={t30} onWheel={_temp} onChange={t31} placeholder={t32} />;
              $[88] = disabled;
              $[89] = t30;
              $[90] = t31;
              $[91] = t32;
              $[92] = t33;
            } else {
              t33 = $[92];
            }
            t29 = t33;
            break bb0;
          }
        }
      case "rating-star":
        {
          const t30 = !question.is_required;
          const t31 = question?.answer;
          let t32;
          if ($[93] !== handleChange) {
            t32 = event_5 => handleChange(`answer`, event_5);
            $[93] = handleChange;
            $[94] = t32;
          } else {
            t32 = $[94];
          }
          let t33;
          if ($[95] !== question.linked_choices) {
            t33 = question.linked_choices?.map(_temp2);
            $[95] = question.linked_choices;
            $[96] = t33;
          } else {
            t33 = $[96];
          }
          let t34;
          if ($[97] !== disabled || $[98] !== t30 || $[99] !== t31 || $[100] !== t32 || $[101] !== t33) {
            t34 = <RatingStarField canSelectNoOption={t30} isDisabled={disabled} value={t31} onChange={t32} choices={t33} />;
            $[97] = disabled;
            $[98] = t30;
            $[99] = t31;
            $[100] = t32;
            $[101] = t33;
            $[102] = t34;
          } else {
            t34 = $[102];
          }
          t29 = t34;
          break bb0;
        }
      case "datetime":
        {
          switch (question.personalization?.option) {
            case "date":
              {
                let t30;
                if ($[103] !== handleChange) {
                  t30 = event_4 => handleChange(`answer`, event_4);
                  $[103] = handleChange;
                  $[104] = t30;
                } else {
                  t30 = $[104];
                }
                let t31;
                if ($[105] !== question.answer || $[106] !== t30) {
                  t31 = <DateField value={question.answer} onChange={t30} />;
                  $[105] = question.answer;
                  $[106] = t30;
                  $[107] = t31;
                } else {
                  t31 = $[107];
                }
                t29 = t31;
                break bb0;
              }
            case "time":
              {
                let t30;
                if ($[108] !== handleChange) {
                  t30 = event_3 => handleChange(`answer`, event_3);
                  $[108] = handleChange;
                  $[109] = t30;
                } else {
                  t30 = $[109];
                }
                let t31;
                if ($[110] !== question.answer || $[111] !== t30) {
                  t31 = <HourField value={question.answer} onChange={t30} />;
                  $[110] = question.answer;
                  $[111] = t30;
                  $[112] = t31;
                } else {
                  t31 = $[112];
                }
                t29 = t31;
                break bb0;
              }
            default:
              {
                let t30;
                if ($[113] !== handleChange) {
                  t30 = event_2 => handleChange(`answer`, event_2);
                  $[113] = handleChange;
                  $[114] = t30;
                } else {
                  t30 = $[114];
                }
                let t31;
                if ($[115] !== question.answer || $[116] !== t30) {
                  t31 = <DateHourField value={question.answer} onChange={t30} readonly={false} />;
                  $[115] = question.answer;
                  $[116] = t30;
                  $[117] = t31;
                } else {
                  t31 = $[117];
                }
                t29 = t31;
                break bb0;
              }
          }
        }
      case "file":
        {
          let t30;
          if ($[118] !== files || $[119] !== handleRemove || $[120] !== setFilesAndPropagate) {
            t30 = <FileUploader authorizedFiles={availableExt} maxSizeInMo={10} isMultiple={false} files={files} setFiles={setFilesAndPropagate} handleRemoveFile={handleRemove} isAlert={false} isLoadingFile={false} />;
            $[118] = files;
            $[119] = handleRemove;
            $[120] = setFilesAndPropagate;
            $[121] = t30;
          } else {
            t30 = $[121];
          }
          t29 = t30;
          break bb0;
        }
      case "checkbox":
        {
          let t30;
          if ($[122] !== question) {
            t30 = (isQuestionOverflowed(question as Question) ? question.linked_choices?.map(_temp3) : question.linked_choices) ?? [];
            $[122] = question;
            $[123] = t30;
          } else {
            t30 = $[123];
          }
          let t31;
          if ($[124] !== handleChange) {
            t31 = (i, value_0) => handleChange(`linked_choices[${i}].is_selected`, value_0);
            $[124] = handleChange;
            $[125] = t31;
          } else {
            t31 = $[125];
          }
          const t32 = question as Question;
          let t33;
          if ($[126] !== t32) {
            t33 = isQuestionOverflowed(t32);
            $[126] = t32;
            $[127] = t33;
          } else {
            t33 = $[127];
          }
          const t34 = question.choices_limit ?? undefined;
          let t35;
          if ($[128] !== t30 || $[129] !== t31 || $[130] !== t33 || $[131] !== t34) {
            t35 = <CheckBoxField choices={t30} onChange={t31} readOnly={false} hideTooltip={t33} limit={t34} />;
            $[128] = t30;
            $[129] = t31;
            $[130] = t33;
            $[131] = t34;
            $[132] = t35;
          } else {
            t35 = $[132];
          }
          let t36;
          if ($[133] !== errorMap || $[134] !== handleChange || $[135] !== indexLabel || $[136] !== question.linked_choices || $[137] !== questionPath || $[138] !== sessions) {
            t36 = question.linked_choices?.flatMap((choice_5, choiceIndex_0) => (choice_5.linked_questions ?? []).map((linkedQuestion, linkedQuestionIndex) => linkedQuestion.is_visible && choice_5.is_selected ? <SurveyTakingQuestion key={linkedQuestion.id} question={linkedQuestion} indexLabel={makeLinkedQuestionLabel(indexLabel, choiceIndex_0, linkedQuestionIndex, (choice_5.linked_questions ?? []).length)} questionPath={`${questionPath}.linked_choices[${choiceIndex_0}].linked_questions[${linkedQuestionIndex}]`} handleChange={(path_0, value_1, oldValue_0) => handleChange(`linked_choices[${choiceIndex_0}].linked_questions[${linkedQuestionIndex}].${path_0}`, value_1, oldValue_0)} errorMap={errorMap} sessions={sessions} disabled={isQuestionOverflowed(linkedQuestion)} /> : null));
            $[133] = errorMap;
            $[134] = handleChange;
            $[135] = indexLabel;
            $[136] = question.linked_choices;
            $[137] = questionPath;
            $[138] = sessions;
            $[139] = t36;
          } else {
            t36 = $[139];
          }
          let t37;
          if ($[140] !== t35 || $[141] !== t36) {
            t37 = <>{t35}{t36}</>;
            $[140] = t35;
            $[141] = t36;
            $[142] = t37;
          } else {
            t37 = $[142];
          }
          t29 = t37;
          break bb0;
        }
      case "radio":
        {
          let t30;
          if ($[143] !== question) {
            t30 = isQuestionOverflowed(question as Question) ? (question.linked_choices ?? []).map(_temp4) : question.linked_choices ?? [];
            $[143] = question;
            $[144] = t30;
          } else {
            t30 = $[144];
          }
          let t31;
          if ($[145] !== handleChange || $[146] !== question.select_value) {
            t31 = event_1 => handleChange(`select_value`, event_1, question.select_value);
            $[145] = handleChange;
            $[146] = question.select_value;
            $[147] = t31;
          } else {
            t31 = $[147];
          }
          const t32 = !question.is_required;
          const t33 = question.select_value;
          const t34 = question as Question;
          let t35;
          if ($[148] !== t34) {
            t35 = isQuestionOverflowed(t34);
            $[148] = t34;
            $[149] = t35;
          } else {
            t35 = $[149];
          }
          let t36;
          if ($[150] !== question.select_value || $[151] !== t30 || $[152] !== t31 || $[153] !== t32 || $[154] !== t35) {
            t36 = <RadioButtonsField choices={t30} onChange={t31} canSelectNoOption={t32} value={t33} readOnly={false} hideTooltip={t35} />;
            $[150] = question.select_value;
            $[151] = t30;
            $[152] = t31;
            $[153] = t32;
            $[154] = t35;
            $[155] = t36;
          } else {
            t36 = $[155];
          }
          let t37;
          if ($[156] !== renderSelectLinked) {
            t37 = renderSelectLinked();
            $[156] = renderSelectLinked;
            $[157] = t37;
          } else {
            t37 = $[157];
          }
          let t38;
          if ($[158] !== t36 || $[159] !== t37) {
            t38 = <>{t36}{t37}</>;
            $[158] = t36;
            $[159] = t37;
            $[160] = t38;
          } else {
            t38 = $[160];
          }
          t29 = t38;
          break bb0;
        }
      case "select":
        {
          let t30;
          if ($[161] !== handleChange || $[162] !== question || $[163] !== t || $[164] !== tooltipLabel) {
            t30 = tooltipLabel ? <Tooltip content={tooltipLabel} placement="top" size="sm" trigger={<div><SelectField choices={isQuestionOverflowed(question as Question) ? (question.linked_choices ?? []).map(_temp5) : question.linked_choices ?? []} onChange={event => handleChange(`select_value`, event)} canSelectNoOption={question.is_required ?? false} value={question.select_value} placeholder={t("choice.select").toString()} disabled={isQuestionOverflowed(question as Question)} /></div>} /> : <div><SelectField choices={isQuestionOverflowed(question as Question) ? (question.linked_choices ?? []).map(_temp6) : question.linked_choices ?? []} onChange={event_0 => handleChange(`select_value`, event_0)} canSelectNoOption={question.is_required ?? false} value={question.select_value} placeholder={t("choice.select").toString()} disabled={isQuestionOverflowed(question as Question)} /></div>;
            $[161] = handleChange;
            $[162] = question;
            $[163] = t;
            $[164] = tooltipLabel;
            $[165] = t30;
          } else {
            t30 = $[165];
          }
          let t31;
          if ($[166] !== renderSelectLinked) {
            t31 = renderSelectLinked();
            $[166] = renderSelectLinked;
            $[167] = t31;
          } else {
            t31 = $[167];
          }
          let t32;
          if ($[168] !== t30 || $[169] !== t31) {
            t32 = <>{t30}{t31}</>;
            $[168] = t30;
            $[169] = t31;
            $[170] = t32;
          } else {
            t32 = $[170];
          }
          t29 = t32;
          break bb0;
        }
      case "participant-block":
        {
          let t30;
          if ($[171] !== errorMap || $[172] !== handleChange || $[173] !== indexLabel || $[174] !== question || $[175] !== questionPath || $[176] !== sessions) {
            t30 = <ParticipantBlock sessions={sessions} question={question} path={questionPath} handleChange={handleChange} indexLabel={indexLabel} errorMap={errorMap} />;
            $[171] = errorMap;
            $[172] = handleChange;
            $[173] = indexLabel;
            $[174] = question;
            $[175] = questionPath;
            $[176] = sessions;
            $[177] = t30;
          } else {
            t30 = $[177];
          }
          t29 = t30;
          break bb0;
        }
    }
    t29 = undefined;
  }
  const t30 = question?.additional_info;
  let t31;
  if ($[178] !== t30) {
    t31 = <QuestionHelper type="info">{t30}</QuestionHelper>;
    $[178] = t30;
    $[179] = t31;
  } else {
    t31 = $[179];
  }
  let t32;
  if ($[180] !== t26 || $[181] !== t28 || $[182] !== t29 || $[183] !== t31) {
    t32 = <div className="flex flex-col gap-6 p-6">{t26}{t28}{t29}{t31}</div>;
    $[180] = t26;
    $[181] = t28;
    $[182] = t29;
    $[183] = t31;
    $[184] = t32;
  } else {
    t32 = $[184];
  }
  let t33;
  if ($[185] !== t24 || $[186] !== t32) {
    t33 = <div className="border-border flex flex-col rounded-lg rounded-tl-none border">{t24}{t25}{t32}</div>;
    $[185] = t24;
    $[186] = t32;
    $[187] = t33;
  } else {
    t33 = $[187];
  }
  let t34;
  if ($[188] !== t15 || $[189] !== t17 || $[190] !== t33) {
    t34 = <div className={t15}>{t17}{t33}</div>;
    $[188] = t15;
    $[189] = t17;
    $[190] = t33;
    $[191] = t34;
  } else {
    t34 = $[191];
  }
  return t34;
};
function _temp(event_6) {
  return event_6.currentTarget.blur();
}
function _temp2(choice_6) {
  return choice_6.label;
}
function _temp3(choice_4) {
  return {
    ...choice_4,
    is_limit_overflowed: true
  };
}
function _temp4(choice_3) {
  return {
    ...choice_3,
    is_limit_overflowed: true
  };
}
function _temp5(choice_1) {
  return {
    ...choice_1,
    is_limit_overflowed: true
  };
}
function _temp6(choice_2) {
  return {
    ...choice_2,
    is_limit_overflowed: true
  };
}